import React, { useState, useEffect, useCallback } from 'react';
import { Target, Plus, Trash2, Save, ChevronDown, ChevronUp } from 'lucide-react';
import { formatMemberDisplayName, getMemberAssigneeId } from '@saas-foundation/shared-frontend';
import {
  fetchTeamQueueGoalsForService,
  patchTeamQueueGoalsForService,
  saasGoalToPanelRow,
  panelRowToSaaSGoal,
  migrateLegacyGoalsToSaaS,
  subscribeSaaSAuthUpdated,
  dispatchTeamQueueGoalsChanged,
} from './teamQueueGoalsApi.js';

const newGoalId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `goal-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/**
 * Supervisor: configure per-member throughput goals (daily / weekly / date range).
 * Persists to SaaS PATCH /organizations/{id}/team-queue-goals with this service's rcm_service_code.
 * Complements Task Manager and the unified Work Queue (see `workqueue/rcmWorkDomain.js`).
 *
 * @param {object} props
 * @param {string} props.rcmServiceCode — canonical code, e.g. claims_processing
 * @param {string|null} props.organizationId
 * @param {boolean} props.isSupervisor
 * @param {object[]} [props.availableUsers] — org members from SaaS
 * @param {object} [props.settingsData] — optional legacy seed (e.g. workQueue.memberGoals) when SaaS empty
 * @param {import('@tanstack/react-query').QueryClient} [props.queryClient]
 * @param {() => void} [props.onSaved] — after successful save (e.g. refresh metrics panel)
 * @param {string} [props.targetUnitLabel] — column header, default "items"
 */
export default function WorkQueueTeamGoalsPanel({
  rcmServiceCode,
  organizationId,
  isSupervisor,
  availableUsers = [],
  settingsData,
  queryClient,
  onSaved,
  targetUnitLabel = 'items',
}) {
  const [expanded, setExpanded] = useState(true);
  const [memberGoals, setMemberGoals] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!organizationId || !rcmServiceCode) return;
      try {
        const saas = await fetchTeamQueueGoalsForService(organizationId, rcmServiceCode);
        if (cancelled) return;
        if (Array.isArray(saas) && saas.length > 0) {
          setMemberGoals(saas.map(saasGoalToPanelRow).filter(Boolean));
          return;
        }
      } catch (e) {
        console.warn('[WorkQueueTeamGoals] SaaS goals fetch failed', e);
      }
      if (cancelled) return;
      const g = settingsData?.workQueue?.memberGoals;
      const legacy = Array.isArray(g) ? g.map((x) => ({ ...x })) : [];
      if (legacy.length > 0) {
        // Migrate Denial/local settings → SaaS (source of truth)
        const migrated = await migrateLegacyGoalsToSaaS(organizationId, rcmServiceCode, legacy);
        if (!cancelled) setMemberGoals(migrated.length ? migrated : legacy);
        return;
      }
      if (!cancelled) setMemberGoals([]);
    };
    load();
    const unsub = subscribeSaaSAuthUpdated(() => {
      load();
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, [organizationId, rcmServiceCode, settingsData]);

  const humanOptions = React.useMemo(() => {
    const list = Array.isArray(availableUsers) ? availableUsers : [];
    return list.filter((m) => {
      const email = (m?.user?.email || m?.email || '').toLowerCase();
      return email !== 'kai@system.rcm.internal';
    });
  }, [availableUsers]);

  const addRow = useCallback(() => {
    setMemberGoals((prev) => [
      ...prev,
      {
        id: newGoalId(),
        assigneeType: 'human',
        memberLabel: '',
        assigneeUserId: null,
        period: 'daily',
        targetCount: 20,
        rangeStart: '',
        rangeEnd: '',
      },
    ]);
  }, []);

  const removeRow = useCallback((id) => {
    setMemberGoals((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const updateRow = useCallback((id, patch) => {
    setMemberGoals((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  const handleSave = async () => {
    if (!organizationId || !rcmServiceCode) return;
    setSaving(true);
    setSaveStatus('');
    try {
      const cleaned = memberGoals
        .map((g) => ({
          id: g.id || newGoalId(),
          assigneeType: g.assigneeType === 'kai' ? 'kai' : 'human',
          memberLabel: g.assigneeType === 'kai' ? '' : String(g.memberLabel || '').trim(),
          assigneeUserId: g.assigneeUserId || null,
          period: ['daily', 'weekly', 'date_range'].includes(g.period) ? g.period : 'daily',
          targetCount: Math.max(0, parseInt(g.targetCount, 10) || 0),
          rangeStart: g.period === 'date_range' && g.rangeStart ? String(g.rangeStart).slice(0, 10) : null,
          rangeEnd: g.period === 'date_range' && g.rangeEnd ? String(g.rangeEnd).slice(0, 10) : null,
        }))
        .filter((g) => g.assigneeType === 'kai' || g.memberLabel.length > 0);

      const apiGoals = cleaned.map((g) => panelRowToSaaSGoal(g, rcmServiceCode));
      await patchTeamQueueGoalsForService(organizationId, rcmServiceCode, apiGoals);
      if (queryClient && organizationId) {
        queryClient.invalidateQueries(['teamQueueGoals', organizationId]);
        queryClient.invalidateQueries(['settings', organizationId]);
        queryClient.invalidateQueries(['settings', organizationId, 'team-analytics']);
        queryClient.invalidateQueries(['teamAnalyticsDenial', organizationId]);
      }
      dispatchTeamQueueGoalsChanged({ organizationId, rcmServiceCode });
      if (typeof onSaved === 'function') onSaved();
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(''), 2500);
    } catch (e) {
      console.error('[WorkQueueTeamGoals]', e);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  if (!isSupervisor) return null;

  return (
    <div className="mb-6 bg-white rounded-lg shadow border border-indigo-100 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-indigo-50 border-b border-indigo-100 text-left"
      >
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-indigo-600" />
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Team queue goals</h3>
            <p className="text-xs text-gray-600">
              App-level goals (SaaS). Set daily, weekly, or date-range targets per teammate and Kai; progress vs
              actuals is shown below on this tab.
            </p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-gray-500 shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500 shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="p-4 space-y-3">
          {memberGoals.length === 0 ? (
            <p className="text-sm text-gray-500">No goals yet. Add a row for each person (including Kai).</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-gray-600">
                    <th className="py-2 pr-3 font-medium">Assignee</th>
                    <th className="py-2 pr-3 font-medium">Period</th>
                    <th className="py-2 pr-3 font-medium">Target ({targetUnitLabel})</th>
                    <th className="py-2 pr-3 font-medium">Date range</th>
                    <th className="py-2 w-10" />
                  </tr>
                </thead>
                <tbody>
                  {memberGoals.map((row) => (
                    <tr key={row.id} className="border-b border-gray-100 align-top">
                      <td className="py-2 pr-3">
                        <select
                          value={row.assigneeType === 'kai' ? 'kai' : row.memberLabel || ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === 'kai') {
                              updateRow(row.id, { assigneeType: 'kai', memberLabel: '', assigneeUserId: null });
                            } else {
                              const m = humanOptions.find((hm) => formatMemberDisplayName(hm) === v);
                              updateRow(row.id, {
                                assigneeType: 'human',
                                memberLabel: v,
                                assigneeUserId: m ? getMemberAssigneeId(m) || null : null,
                              });
                            }
                          }}
                          className="w-full max-w-[240px] border border-gray-300 rounded px-2 py-1.5 text-sm"
                        >
                          <option value="">Select…</option>
                          <option value="kai">Kai (AI coworker)</option>
                          {humanOptions.map((m) => {
                            const label = formatMemberDisplayName(m);
                            const id = getMemberAssigneeId(m);
                            return (
                              <option key={id || label} value={label}>
                                {label}
                              </option>
                            );
                          })}
                        </select>
                      </td>
                      <td className="py-2 pr-3">
                        <select
                          value={row.period}
                          onChange={(e) => updateRow(row.id, { period: e.target.value })}
                          className="border border-gray-300 rounded px-2 py-1.5 text-sm"
                        >
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly (Mon–Sun)</option>
                          <option value="date_range">Date range</option>
                        </select>
                      </td>
                      <td className="py-2 pr-3">
                        <input
                          type="number"
                          min="0"
                          value={row.targetCount}
                          onChange={(e) => updateRow(row.id, { targetCount: parseInt(e.target.value, 10) || 0 })}
                          className="w-24 border border-gray-300 rounded px-2 py-1.5 text-sm"
                        />
                      </td>
                      <td className="py-2 pr-3">
                        {row.period === 'date_range' ? (
                          <div className="flex flex-col sm:flex-row gap-1 sm:items-center">
                            <input
                              type="date"
                              value={row.rangeStart || ''}
                              onChange={(e) => updateRow(row.id, { rangeStart: e.target.value })}
                              className="border border-gray-300 rounded px-2 py-1 text-xs"
                            />
                            <span className="text-gray-400 hidden sm:inline">–</span>
                            <input
                              type="date"
                              value={row.rangeEnd || ''}
                              onChange={(e) => updateRow(row.id, { rangeEnd: e.target.value })}
                              className="border border-gray-300 rounded px-2 py-1 text-xs"
                            />
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="py-2">
                        <button
                          type="button"
                          onClick={() => removeRow(row.id)}
                          className="p-1.5 text-gray-500 hover:text-red-600 rounded"
                          title="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button
              type="button"
              onClick={addRow}
              className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium bg-gray-100 text-gray-800 hover:bg-gray-200"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add goal
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !organizationId}
              className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4 mr-1" />
              {saving ? 'Saving…' : 'Save goals'}
            </button>
            {saveStatus === 'success' && <span className="text-sm text-green-600">Saved</span>}
            {saveStatus === 'error' && <span className="text-sm text-red-600">Save failed</span>}
          </div>
        </div>
      )}
    </div>
  );
}
